/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Grid } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useState } from 'react';

import { ConfigRevisionListItem, NodeConfig, PaginatedConfigRevisionResponse } from '@/client';
import { GuardedLink } from '@/components/GuardedLink';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { RelativeTime } from '@/components/RelativeTime';
import { useConfigRevisionByIdQuery } from '@/hooks/query/useConfigRevisionByIdQuery';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ConfigurationDetailsRevisionCode } from '@/pages/NodeConfigDetails/ConfigurationDetailsRevisionCode';
import { Card } from '@/primitives/Card';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { Radio } from '@/primitives/Radio';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import './NodeConfigDetailsRevisions.css';

export type ConfigurationDetailsRevisionsProps = {
  tenantID: string;
  nodeConfig: NodeConfig;
  configRevisionsData: PaginatedConfigRevisionResponse;
};

const { useBreakpoint } = Grid;

export function NodeConfigDetailsRevisions({
  tenantID,
  nodeConfig,
  configRevisionsData,
}: ConfigurationDetailsRevisionsProps) {
  const { t } = useMimicTranslation('nodeConfigurationDetail');
  const screens = useBreakpoint();

  const [selectedRevisionID, setSelectedRevisionID] = useState<string | null>(null);
  const [showRevisionCode, setShowRevisionCode] = useState(false);

  const onClose = () => {
    setShowRevisionCode(false);
  };

  const onOpen = () => {
    setShowRevisionCode(true);
  };

  const allColumns = getRevisionColumns(tenantID, nodeConfig.id, selectedRevisionID, t, setSelectedRevisionID, onOpen);

  const selectColumns = allColumns.slice(0, 3);

  // The list endpoint no longer returns the config source, so fetch the full
  // revision (including source) from the get-one endpoint when one is selected.
  const selectedRevision = configRevisionsData.data?.find((revision) => revision.id === selectedRevisionID);

  const configRevisionQuery = useConfigRevisionByIdQuery({
    nodeConfigId: nodeConfig.id,
    // Only fetch while the code panel is open to avoid background refetches once it is closed.
    configRevisionNumber: showRevisionCode ? selectedRevision?.revisionNumber ?? 0 : 0,
  });

  const colSize = screens.xl ? 12 : 24;
  const padding = screens.sm ? 'md' : 'xs';

  if (!showRevisionCode) {
    return (
      <Card padding={padding} gap="sm">
        <PaginatedTable
          columns={allColumns}
          data={configRevisionsData}
          rowKey={(revision: ConfigRevisionListItem) => revision.id}
        />
      </Card>
    );
  }

  return (
    <Card padding="xs" gap="sm">
      <Row gutter={[16, 16]}>
        <Flex wrap={!screens.xl} full>
          <Col xs={24} lg={colSize}>
            <div style={{ width: '100%' }}>
              <PaginatedTable
                columns={selectColumns}
                data={configRevisionsData}
                rowKey={(revision: ConfigRevisionListItem) => revision.id}
              />
            </div>
          </Col>
          <Col xs={24} lg={colSize}>
            <QueryWrapper queryResult={configRevisionQuery}>
              {(configRevisionData) => (
                <ConfigurationDetailsRevisionCode configRevision={configRevisionData.data} onClose={onClose} />
              )}
            </QueryWrapper>
          </Col>
        </Flex>
      </Row>
    </Card>
  );
}

function getRevisionColumns(
  tenantID: string,
  configID: string,
  selectedRevisionID: string | null,
  t: MimicTranslationFunction<'nodeConfigurationDetail'>,
  setSelectedRevisionID: (revisionID: string) => void,
  onOpen: (isOpen: boolean) => void,
) {
  const columns: ColumnType<ConfigRevisionListItem>[] = [
    {
      dataIndex: 'select',
      width: '44px',
      render: (_, revision: ConfigRevisionListItem) => {
        return (
          <Radio
            dataTestId="select-config-revision"
            checked={selectedRevisionID === revision.id}
            onChange={() => setSelectedRevisionID(revision.id)}
            onClick={() => onOpen(true)}
          />
        );
      },
    },
    {
      title: <span data-testid="revisionNumber">#</span>,
      dataIndex: 'revisionNumber',
      width: '72px',
      render: (_, revision: ConfigRevisionListItem) => {
        return (
          <Text type="default" size="md" className={selectedRevisionID === revision.id ? 'selected' : ''}>
            {revision.revisionNumber}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="revisionDescription">{t('revisionsTable.revisionDesription')}</span>,
      dataIndex: 'description',
      render: (_, revision: ConfigRevisionListItem) => {
        return (
          <GuardedLink
            to={`/tenants/${tenantID}/node-configs/${configID}/revisions/${revision.revisionNumber}`}
            style={{ fontSize: 'inherit', fontFamily: 'DM Mono' }}
            dataTestId="revision-link"
            requiredRole="editor"
          >
            <Text type="link" size="md">
              {revision.description}
            </Text>
          </GuardedLink>
        );
      },
    },
    {
      title: <span data-testid="author">{t('revisionsTable.author')}</span>,
      dataIndex: 'author',
      width: '172px',
      render: (_, revision: ConfigRevisionListItem) => {
        return (
          <Text type="default" size="md">
            {revision.createdBy.displayName}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="created">{t('revisionsTable.created')}</span>,
      dataIndex: 'createdAt',
      width: '172px',
      render: (_, revision: ConfigRevisionListItem) => {
        return (
          <Text type="default" size="md">
            <RelativeTime date={revision.createdAt} />
          </Text>
        );
      },
    },
  ];

  return columns;
}
